/**
 * PhotoLightbox - Full-screen image viewer.
 *
 * Renders a single photo at the largest size that fits the viewport
 * with a dim backdrop and a close button in the top-right. Used by
 * the WO workflow checklist when the user taps the expand icon on a
 * captured thumbnail, but the contract is generic — anything with
 * an image URL can host this.
 *
 * Behavior:
 *   - Closes on Escape key
 *   - Closes on backdrop click (clicking the image itself does NOT
 *     close, so users can pan/inspect without losing position)
 *   - Locks background scroll while open
 *   - Optional caption + area metadata renders below the image when
 *     supplied so the user can confirm what they're looking at
 *
 * Stays a thin atom-of-an-organism: no header chrome, no carousel,
 * no zoom controls. If we need any of those later, they go in here.
 */
import React, { useEffect } from 'react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface PhotoLightboxProps {
    /** Whether the viewer is currently visible. */
    isOpen: boolean;
    /** Absolute URL or data: URL to display. */
    imageUrl: string | null;
    /** Optional caption surfaced under the image. */
    caption?: string | null;
    /** Optional area label surfaced under the image (e.g. "Entry"). */
    area?: string | null;
    /** Close handler — fires on Escape, X click, and backdrop click. */
    onClose: () => void;
    /** Optional outer className merged onto the root element. */
    className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const PhotoLightbox: React.FC<PhotoLightboxProps> = ({
    isOpen,
    imageUrl,
    caption,
    area,
    onClose,
    className,
}) => {
    /* Lock background scroll while the lightbox is open. Stack-safe, so
     * closing it over the workflow overlay keeps the page frozen. */
    useBodyScrollLock(isOpen);

    /* Close on Escape key. Mounted only while open so we don't leak
     * a global listener across the app. */
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen || !imageUrl) return null;

    return (
        /* Sit at z-[60] so we layer above WorkOrderWorkflow's z-50
         * overlay — this lightbox can be opened from inside it. */
        <div
            className={['photo-lightbox', 'fixed inset-0 z-[60] flex flex-col items-center justify-center bg-black/90', className ?? ''].join(' ')}
            role="dialog"
            aria-modal="true"
            aria-label="Photo viewer"
            onClick={onClose}
            data-ref="organism-photo-lightbox"
        >
            {/* Close button — fixed top-right, large 44px hit target
             *  for touch. Stops propagation so backdrop click handler
             *  doesn't double-fire. */}
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                }}
                className="absolute top-16px right-16px w-44px h-44px rounded-full flex items-center justify-center text-white bg-black/40 hover:bg-black/60 transition-colors"
                aria-label="Close photo viewer"
            >
                <svg className="w-24px h-24px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
            </button>

            {/* Image — clicks on it do NOT close the viewer so users
             *  can move their finger / cursor over it without
             *  losing the view. */}
            <img
                src={imageUrl}
                alt={caption ?? 'Captured photo'}
                onClick={(e) => e.stopPropagation()}
                className="photo-lightbox__image max-w-[95vw] max-h-[80vh] object-contain rounded-md shadow-2xl"
            />

            {/* Optional metadata panel — only renders when at least
             *  one of caption/area is non-empty so we don't surface
             *  empty UI under bare images. */}
            {(caption || area) && (
                <div
                    onClick={(e) => e.stopPropagation()}
                    className="mt-16px max-w-[95vw] px-16px py-10px bg-black/50 rounded-md text-white text-13px font-sans"
                >
                    {area && (
                        <div className="text-12px uppercase tracking-wide text-white/70 mb-2px">
                            {area}
                        </div>
                    )}
                    {caption && <div className="leading-snug">{caption}</div>}
                </div>
            )}
        </div>
    );
};

export default PhotoLightbox;
